import { useEffect, useState } from "react";
import { peerFormApi, withPeerFormToken, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { HeartHandshake, ShieldCheck, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
// Logo sesuai latar terang/gelap (sama dgn AuthShell) + tagline sbg teks (lib/brand.js); logo.png lama
// berwarna putih (utk sidebar teal) & memuat tagline lama di dalam gambar.
import logoTerang from "../assets/aura-logo-terang.png";
import logoGelap from "../assets/aura-logo-gelap.png";
import { TAGLINE } from "../lib/brand";

// Kunci sessionStorage (BUKAN localStorage "simonev_token" yg dipakai akun admin --
// lihat lib/api.js peerFormApi/withPeerFormToken) supaya sesi form ini sama sekali
// terpisah dari sesi login admin AURA yang mungkin aktif di browser yang sama.
const FORM_TOKEN_KEY = "peer_form_token";

const KATEGORI_LABEL = { setara: "Sejawat Setingkat", junior: "Sejawat Junior", senior: "Sejawat Senior" };
const KATEGORI_DESC = {
    setara: "Semester yang sama dengan Anda",
    junior: "Semester lebih rendah dari Anda",
    senior: "Semester lebih tinggi dari Anda",
};

export default function PeerAssessmentPublic() {
    const [step, setStep] = useState("npm"); // npm -> confirm -> pilih -> form -> selesai
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const [npm, setNpm] = useState("");
    const [pendingToken, setPendingToken] = useState(null);
    const [confirmInfo, setConfirmInfo] = useState(null);
    const [formToken, setFormToken] = useState(() => sessionStorage.getItem(FORM_TOKEN_KEY) || null);

    const [criteria, setCriteria] = useState([]);
    const [categories, setCategories] = useState([]);
    const [progress, setProgress] = useState(null);
    const [targets, setTargets] = useState(null);
    const [selectedTarget, setSelectedTarget] = useState(null);

    const [scores, setScores] = useState({});
    const [notes, setNotes] = useState({});
    const [qualitative, setQualitative] = useState({ kekuatan: "", area_pengembangan: "", saran: "" });

    useEffect(() => {
        peerFormApi.get("/peer-assessment/criteria").then(({ data }) => {
            setCriteria(data.criteria);
            setCategories(data.categories);
        }).catch(() => {});
    }, []);

    // Kalau ada form_token tersimpan dari sesi sebelumnya (mis. reload halaman
    // sebelum selesai menilai 3 sejawat), lanjutkan langsung ke daftar sejawat --
    // tidak perlu ulang dari NPM selama tokennya belum kedaluwarsa (2 jam).
    useEffect(() => {
        if (formToken && step === "npm") loadPilihan(formToken);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadPilihan = async (token) => {
        setBusy(true);
        setError("");
        try {
            const [p, t] = await Promise.all([
                peerFormApi.get("/peer-assessment/my-progress", withPeerFormToken(token)),
                peerFormApi.get("/peer-assessment/eligible-targets", withPeerFormToken(token)),
            ]);
            setProgress(p.data);
            setTargets(t.data);
            setStep("pilih");
        } catch (e) {
            sessionStorage.removeItem(FORM_TOKEN_KEY);
            setFormToken(null);
            setError(formatApiErrorDetail(e.response?.data?.detail));
            setStep("npm");
        } finally {
            setBusy(false);
        }
    };

    const submitNpm = async () => {
        if (!npm.trim()) return;
        setBusy(true);
        setError("");
        try {
            const { data } = await peerFormApi.post("/peer-assessment/lookup-npm", { npm: npm.trim() });
            setPendingToken(data.pending_token);
            setConfirmInfo(data);
            setStep("confirm");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const confirmIdentity = async () => {
        setBusy(true);
        setError("");
        try {
            const { data } = await peerFormApi.post("/peer-assessment/confirm", { pending_token: pendingToken });
            sessionStorage.setItem(FORM_TOKEN_KEY, data.form_token);
            setFormToken(data.form_token);
            await loadPilihan(data.form_token);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const pilihTarget = (kategori, target) => {
        setSelectedTarget({ ...target, kategori });
        setScores({});
        setNotes({});
        setQualitative({ kekuatan: "", area_pengembangan: "", saran: "" });
        setStep("form");
    };

    const submitPenilaian = async () => {
        const belumDiisi = criteria.some((c) => !scores[c.criteria_id]);
        if (belumDiisi) return toast.error("Semua 10 kriteria wajib diberi skor.");
        setBusy(true);
        try {
            await peerFormApi.post("/peer-assessment/submit", {
                target_ppds_id: selectedTarget.id,
                scores: criteria.map((c) => ({
                    criteria_id: c.criteria_id, score: scores[c.criteria_id], note: notes[c.criteria_id] || "",
                })),
                ...qualitative,
            }, withPeerFormToken(formToken));
            setStep("selesai");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const nilaiSejawatLain = () => loadPilihan(formToken);

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4">
            <div className="max-w-2xl mx-auto space-y-4">
                <div className="flex justify-center">
                    <div className="flex flex-col items-center gap-1 text-center" data-testid="peer-brand">
                        <img src={logoTerang} alt="AURA PPDS" className="block h-10 w-auto dark:hidden" />
                        <img src={logoGelap} alt="AURA PPDS" className="hidden h-10 w-auto dark:block" />
                        <p className="text-xs text-slate-500 text-balance">{TAGLINE}</p>
                    </div>
                </div>
                <Card className="border-slate-200 shadow-sm">
                    <CardContent className="p-6 space-y-5">
                        <div className="flex items-center gap-2">
                            <HeartHandshake className="w-6 h-6 text-teal-600" />
                            <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                                Peer Assessment Budaya CIPTO
                            </h1>
                        </div>

                        {error && (
                            <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3">{error}</div>
                        )}

                        {step === "npm" && (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-600">
                                    Masukkan NPM Anda untuk mulai menilai sejawat PPDS di kelompok kerja yang sama.
                                </p>
                                <div>
                                    <Label className="text-xs font-semibold uppercase text-slate-600">NPM</Label>
                                    <Input
                                        value={npm}
                                        onChange={(e) => setNpm(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && submitNpm()}
                                        placeholder="Masukkan NPM Anda"
                                        data-testid="peer-npm-input"
                                    />
                                </div>
                                <Button className="w-full bg-[#00B9AD] hover:bg-[#60C0D0]" disabled={busy} onClick={submitNpm} data-testid="peer-npm-submit">
                                    {busy ? "Memeriksa..." : "Lanjutkan"}
                                </Button>
                                <div className="flex items-start gap-2 text-xs text-slate-400 pt-2 border-t">
                                    <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                                    Identitas penilai tidak pernah dicatat pada hasil penilaian — proses ini anonim.
                                </div>
                            </div>
                        )}

                        {step === "confirm" && confirmInfo && (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-600">Konfirmasi bahwa data berikut adalah data Anda:</p>
                                <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                                    <div><span className="text-slate-500 text-sm">Nama: </span><span className="font-semibold">{confirmInfo.nama_tersamar}</span></div>
                                    <div><span className="text-slate-500 text-sm">Departemen/KSM: </span><span className="font-semibold">{confirmInfo.prodi_nama}</span></div>
                                    <div><span className="text-slate-500 text-sm">Semester: </span><span className="font-semibold">{confirmInfo.semester}</span></div>
                                    <div><span className="text-slate-500 text-sm">Periode: </span><span className="font-semibold">{confirmInfo.period_nama}</span></div>
                                </div>
                                <p className="text-xs text-slate-400">
                                    Langkah ini hanya untuk memastikan NPM yang Anda masukkan sudah benar, bukan proses login.
                                </p>
                                <div className="flex gap-2">
                                    <Button variant="outline" className="flex-1" onClick={() => setStep("npm")}>Bukan Saya</Button>
                                    <Button className="flex-1 bg-[#00B9AD] hover:bg-[#60C0D0]" disabled={busy} onClick={confirmIdentity} data-testid="peer-confirm-yes">
                                        {busy ? "Memproses..." : "Ya, Ini Saya"}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {step === "pilih" && targets && progress && (
                            <div className="space-y-4">
                                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800">
                                    Progress Anda periode ini: <span className="font-bold">{progress.total_selesai} dari {progress.target_minimal}</span> sejawat dinilai.
                                    {progress.kategori_belum.length > 0 && (
                                        <> Kategori yang belum: {progress.kategori_belum.map((k) => KATEGORI_LABEL[k]).join(", ")}.</>
                                    )}
                                </div>
                                {["setara", "junior", "senior"].map((k) => (
                                    <div key={k}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="font-bold text-sm text-slate-700">{KATEGORI_LABEL[k]}</h3>
                                            <Badge variant="outline" className="text-[10px]">{KATEGORI_DESC[k]}</Badge>
                                        </div>
                                        {targets[k].length === 0 ? (
                                            <p className="text-xs text-slate-400 pl-1">Tidak ada sejawat tersedia di kategori ini.</p>
                                        ) : (
                                            <div className="space-y-1.5">
                                                {targets[k].map((t) => (
                                                    <button
                                                        key={t.id}
                                                        onClick={() => pilihTarget(k, t)}
                                                        className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-teal-400 hover:bg-teal-50 text-sm font-medium text-slate-700"
                                                    >
                                                        {t.nama}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {step === "form" && selectedTarget && (
                            <div className="space-y-5">
                                <button onClick={() => setStep("pilih")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
                                    <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke daftar
                                </button>
                                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                                    Menilai: <span className="font-bold">{selectedTarget.nama}</span> ({KATEGORI_LABEL[selectedTarget.kategori]})
                                </div>

                                {categories.map((cat) => (
                                    <div key={cat} className="space-y-3">
                                        <h3 className="font-bold text-sm text-teal-700 border-b pb-1">{cat}</h3>
                                        {criteria.filter((c) => c.category === cat).map((c) => (
                                            <div key={c.criteria_id} className="space-y-2">
                                                <p className="text-sm text-slate-700">{c.text}</p>
                                                <div className="flex gap-2">
                                                    {[1, 2, 3, 4, 5].map((v) => (
                                                        <button
                                                            key={v}
                                                            type="button"
                                                            onClick={() => setScores((s) => ({ ...s, [c.criteria_id]: v }))}
                                                            className={`w-10 h-10 rounded-full border-2 font-bold text-sm transition-colors ${
                                                                scores[c.criteria_id] === v
                                                                    ? "bg-[#00B9AD] border-[#00B9AD] text-white"
                                                                    : "border-slate-300 text-slate-500 hover:border-teal-400"
                                                            }`}
                                                            data-testid={`peer-score-${c.criteria_id}-${v}`}
                                                        >
                                                            {v}
                                                        </button>
                                                    ))}
                                                </div>
                                                <Input
                                                    placeholder="Keterangan (opsional)"
                                                    value={notes[c.criteria_id] || ""}
                                                    onChange={(e) => setNotes((n) => ({ ...n, [c.criteria_id]: e.target.value }))}
                                                    className="text-xs"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                <div className="space-y-3 border-t pt-4">
                                    <h3 className="font-bold text-sm text-slate-700">Catatan Kualitatif (Opsional)</h3>
                                    <div>
                                        <Label className="text-xs text-slate-500">Kekuatan yang menonjol</Label>
                                        <Textarea value={qualitative.kekuatan} onChange={(e) => setQualitative({ ...qualitative, kekuatan: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Area yang perlu dikembangkan</Label>
                                        <Textarea value={qualitative.area_pengembangan} onChange={(e) => setQualitative({ ...qualitative, area_pengembangan: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-500">Saran perbaikan</Label>
                                        <Textarea value={qualitative.saran} onChange={(e) => setQualitative({ ...qualitative, saran: e.target.value })} />
                                    </div>
                                </div>

                                <Button className="w-full bg-[#00B9AD] hover:bg-[#60C0D0]" disabled={busy} onClick={submitPenilaian} data-testid="peer-submit-button">
                                    {busy ? "Menyimpan..." : "Kirim Penilaian"}
                                </Button>
                            </div>
                        )}

                        {step === "selesai" && (
                            <div className="text-center space-y-4 py-4">
                                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                                <div>
                                    <p className="font-bold text-slate-800">Penilaian tersimpan</p>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Penilaian ini tidak bisa diubah lagi, dan identitas Anda tidak tercatat pada hasil ini.
                                    </p>
                                </div>
                                <Button className="w-full bg-[#00B9AD] hover:bg-[#60C0D0]" onClick={nilaiSejawatLain}>
                                    Nilai Sejawat Lain
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
